/**
 * An activity's schedule rule in words, for its card: "Thu–Sat · 21:00, 21:30",
 * "First Sunday of the month · 11:00 · not Jan, Feb, Jul or Aug". The rule
 * format is src/utils/activitySchedule.js; tiers are docs/PLACE_ACTIVITIES.md.
 *
 * Day and month names come from Intl in the display language and lists are
 * joined by formatList, so nothing here is English-only; the words around
 * them come from the `place` catalog (activities.schedule.*), passed in as `t`.
 */
import { formatList } from './stringUtils';
import { formatTime } from './dateUtils';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
// Week order for display: Monday first.
const WEEK = [1, 2, 3, 4, 5, 6, 0];

function dayName(index, locale, style) {
  // 2023-01-01 was a Sunday: day N is the 1st + N.
  const d = new Date(Date.UTC(2023, 0, 1 + index));
  try { return new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(d); } catch (e) { return DAY_KEYS[index]; }
}

function monthName(month1, locale, style = 'short') {
  const d = new Date(Date.UTC(2023, month1 - 1, 15));
  try { return new Intl.DateTimeFormat(locale, { month: style, timeZone: 'UTC' }).format(d); } catch (e) { return String(month1); }
}

function shortDate(dateStr, locale) {
  const d = new Date(dateStr + 'T00:00:00Z');
  try { return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d); } catch (e) { return dateStr; }
}

/** "Thu–Sat", "Daily", "Saturday and Sunday" (Monday-first, runs of 3+
 *  collapsed; one or two days are named in full). */
function daysText(days, t, locale) {
  const set = new Set(days.map(d => DAY_KEYS.indexOf(d)));
  if (set.size === 7) return t('activities.schedule.daily');
  const ordered = WEEK.filter(i => set.has(i));
  const runs = [];
  for (const i of ordered) {
    const last = runs[runs.length - 1];
    if (last && WEEK.indexOf(i) === WEEK.indexOf(last[last.length - 1]) + 1) last.push(i);
    else runs.push([i]);
  }
  const parts = runs.flatMap(r => (r.length >= 3
    ? [t('activities.schedule.dayRange', { from: dayName(r[0], locale, 'short'), to: dayName(r[r.length - 1], locale, 'short') })]
    : r.map(i => dayName(i, locale, ordered.length <= 2 ? 'long' : 'short'))));
  return formatList(parts);
}

/** "10:00 AM–8:00 PM" for one session with an end, "9:00 PM and 9:30 PM" for
 *  several starts; null when there are none. Takes rule times or occurrences. */
export function timesText(times) {
  if (!times || !times.length) return null;
  if (times.length === 1 && times[0].end) return `${formatTime(times[0].start)}–${formatTime(times[0].end)}`;
  return formatList(times.map(x => formatTime(x.start)));
}

function seasonText(rule, t, locale) {
  const full = s => /^\d{4}-/.test(s);
  if (rule.from === undefined && rule.to === undefined) return null;
  if ((rule.from && full(rule.from)) || (rule.to && full(rule.to))) {
    if (rule.from && rule.to) return t('activities.schedule.dateRange', { from: shortDate(rule.from, locale), to: shortDate(rule.to, locale) });
    if (rule.to) return t('activities.schedule.until', { date: shortDate(rule.to, locale) });
    return t('activities.schedule.from', { date: shortDate(rule.from, locale) });
  }
  // A recurring season reads as months ("Jun–Sep") only when it starts on the
  // 1st and ends on a month's last day; otherwise the days matter ("Nov 15 –
  // Mar 15", not "Nov–Mar"). 2024 is a leap year, so 02-29 formats.
  const monthEnd = s => s.slice(3) === String(new Date(Date.UTC(2024, Number(s.slice(0, 2)), 0)).getUTCDate());
  const wholeMonths = (!rule.from || rule.from.slice(3) === '01') && (!rule.to || monthEnd(rule.to));
  const md = s => (wholeMonths ? monthName(Number(s.slice(0, 2)), locale) : shortDate('2024-' + s, locale));
  // One whole month reads as that month ("Jun"), never "Jun–Jun".
  if (rule.from && rule.to && wholeMonths && rule.from.slice(0, 2) === rule.to.slice(0, 2)) return md(rule.from);
  if (rule.from && rule.to) {
    return wholeMonths
      ? t('activities.schedule.dayRange', { from: md(rule.from), to: md(rule.to) })
      : t('activities.schedule.dateRange', { from: md(rule.from), to: md(rule.to) });
  }
  return rule.to ? t('activities.schedule.until', { date: md(rule.to) }) : t('activities.schedule.from', { date: md(rule.from) });
}

/**
 * Several rules that are each "every day, between two full dates, same times"
 * are one calendar of open stretches. Returns { list, times } or null.
 */
function dateRuns(schedule) {
  const rules = schedule.rules || [];
  if (rules.length < 2) return null;
  const full = s => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
  const times = JSON.stringify(rules[0].times || []);
  const ok = rules.every(r => r.days.length === 7 && full(r.from) && full(r.to) && !r.nth && !r.nth_weekend
    && !r.months && !r.not_months && JSON.stringify(r.times || []) === times);
  if (!ok) return null;
  return { list: rules.map(r => ({ from: r.from, to: r.to })).sort((x, y) => (x.from < y.from ? -1 : 1)), times: rules[0].times };
}

function alsoLine(also, t, locale) {
  return t('activities.schedule.also', { dates: formatList(also.map(a => shortDate(a.date, locale))) });
}

// Exactly Friday, Saturday and Sunday: the only days "Friday to Sunday" names.
// A weekend counted from its Friday can still run Saturday and Sunday only.
function isFriToSun(days) {
  return days.length === 3 && ['fri', 'sat', 'sun'].every(d => days.includes(d));
}

/** When a rule block runs, without times or season: "Thu–Sat", "First Sunday of the month". */
function whenText(rule, t, locale) {
  let when;
  if (rule.nth) {
    // "first and last Sunday of the month": one sentence, the ordinals joined,
    // so a second ordinal never starts with a capital mid-line.
    when = t('activities.schedule.nthWeekday', {
      which: formatList(rule.nth.map(n => t(`activities.schedule.ordinalWord.${n === -1 ? 'last' : n}`))),
      day: formatList(rule.days.map(d => dayName(DAY_KEYS.indexOf(d), locale, 'long'))),
    });
  } else if (rule.nth_weekend && rule.weekend_starts === 'fri') {
    // "1st and 3rd Friday to Sunday of the month": counted from the Friday.
    // When it runs on fewer days (Saturday and Sunday), the counting still
    // starts on the Friday, so say so and name the days: in a month that
    // starts on a Saturday, its 1st such weekend is the 8th–9th, not the 1st–2nd.
    when = t('activities.schedule.nthFriToSun', {
      which: formatList(rule.nth_weekend.map(n => t(`activities.schedule.ordinal.${n === -1 ? 'last' : n}`))),
    });
    if (!isFriToSun(rule.days)) when = `${when} · ${daysText(rule.days, t, locale)}`;
  } else if (rule.nth_weekend) {
    when = t('activities.schedule.nthWeekend', {
      which: formatList(rule.nth_weekend.map(n => t(`activities.schedule.ordinal.${n === -1 ? 'last' : n}`))),
      count: rule.nth_weekend.length,
    });
    // "Weekend" means Sat + Sun; say the days when they are anything else
    // (Fri–Sun, Saturday only).
    const plain = rule.days.length === 2 && rule.days.includes('sat') && rule.days.includes('sun');
    if (!plain) when = `${when} · ${daysText(rule.days, t, locale)}`;
  } else {
    when = daysText(rule.days, t, locale);
  }
  return when;
}

function capitalize(line, locale) {
  // Fragments can start lowercase ("last weekend of the month"); a line never does.
  return line.charAt(0).toLocaleUpperCase(locale) + line.slice(1);
}

/** One line per rule block. */
function ruleLine(rule, t, locale) {
  const parts = [whenText(rule, t, locale), timesText(rule.times), seasonText(rule, t, locale)];
  if (rule.not_months) parts.push(t('activities.schedule.notIn', { months: formatList(rule.not_months.map(m => monthName(m, locale)), 'disjunction') }));
  if (rule.months) parts.push(t('activities.schedule.onlyIn', { months: formatList(rule.months.map(m => monthName(m, locale))) }));
  return capitalize(parts.filter(Boolean).join(' · '), locale);
}

/**
 * How often it runs, in a few words, for a card's label: "Every Sunday",
 * "Every Wed–Sun", "Daily", "First Sunday of the month", "On set dates".
 * The first rule block speaks for the activity; the full schedule is on its
 * place. Null when there is no schedule.
 */
export function patternText(schedule, t, locale, { midSentence = false } = {}) {
  if (!schedule) return null;
  const finish = text => (midSentence ? text.charAt(0).toLocaleLowerCase(locale) + text.slice(1) : capitalize(text, locale));
  if (dateRuns(schedule)) return finish(t('activities.schedule.setDates'));
  const rule = (schedule.rules || [])[0];
  if (!rule) return null;
  const plainDays = !rule.nth && !rule.nth_weekend && rule.days.length < 7;
  return finish(plainDays
    ? t('activities.schedule.every', { days: daysText(rule.days, t, locale) })
    : whenText(rule, t, locale));
}

/** "Wed, 21 Oct · 9:00 PM": one upcoming occurrence with its start time. */
export function nextOccurrenceText(o, locale) {
  return [occurrenceText(o, locale), o.start ? formatTime(o.start) : null].filter(Boolean).join(' · ');
}

/** Today's date in a time zone ('YYYY-MM-DD'): the schedule's own calendar. */
export function todayIn(tz, now = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  } catch (e) {
    return now.toISOString().slice(0, 10);
  }
}

/** The time now in a time zone ('HH:MM'): to tell a session still ahead today from one over. */
export function nowTimeIn(tz, now = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: tz || 'UTC', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(now);
  } catch (e) {
    return now.toISOString().slice(11, 16);
  }
}

/**
 * @param {object} schedule  the rule (activity.schedule), or null
 * @param {Function} t       react-i18next t bound to the `place` namespace
 * @param {string} locale    display locale for Intl
 * @param {object} [opts]
 * @param {string} [opts.today]  'YYYY-MM-DD' in the schedule's calendar; defaults to now there
 * @returns {string[]} lines, [] when there is no schedule
 */
export function scheduleLines(schedule, t, locale, { today } = {}) {
  if (!schedule) return [];
  const runs = dateRuns(schedule);
  if (runs) {
    // A calendar of separate stretches (the Mercat Gòtic): "On set dates ·
    // 10:00–20:00", then the stretches still to come. Read rule by rule it
    // would say "Daily · Oct 22 – Oct 23", as if that were the whole season.
    // Past stretches are dropped: the dates carry no year, so last month's
    // would read as next year's.
    const day = today || todayIn(schedule.tz);
    const ahead = runs.list.filter(r => r.to >= day);
    const head = [t('activities.schedule.setDates'), timesText(runs.times)].filter(Boolean).join(' · ');
    const out = [head];
    if (ahead.length) {
      out.push(ahead.map(r => (r.from === r.to ? shortDate(r.from, locale)
        : t('activities.schedule.dateRange', { from: shortDate(r.from, locale), to: shortDate(r.to, locale) }))).join(' · '));
    }
    // Extra dates still to come, even once every stretch is over (they carry
    // no year either, so past ones are dropped the same way).
    const alsoAhead = (schedule.also || []).filter(a => a.date >= day);
    if (alsoAhead.length) out.push(alsoLine(alsoAhead, t, locale));
    return out;
  }
  const lines = (schedule.rules || []).map(r => ruleLine(r, t, locale));
  // Extra dates carry no year: only the ones still to come.
  const alsoAhead = (schedule.also || []).filter(a => a.date >= (today || todayIn(schedule.tz)));
  if (alsoAhead.length) lines.push(alsoLine(alsoAhead, t, locale));
  return lines;
}

/** "Sun, Oct 25" — the date of one upcoming occurrence. Its time is already
 *  on the schedule line above it, so it is not repeated. */
export function occurrenceText(o, locale) {
  const d = new Date(o.date + 'T00:00:00Z');
  try { return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d); } catch (e) { return o.date; }
}

/** "Oct 2026" — when the schedule was checked. */
export function checkedText(dateStr, locale) {
  const d = new Date(dateStr + 'T00:00:00Z');
  try { return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d); } catch (e) { return dateStr; }
}
